import type {
  Coordinate, EditableFloorPlan, MultiPolygonCoordinates, StructuralType,
} from '../models/editor-feature';
import { DEFAULT_FOOTPRINT } from './desk-symbols';
import { DEFAULT_GROUP_OPTIONS, deskGroupSeats, type DeskGroupKind } from './desk-groups';
import {
  addDeskGroup, addIconLabel, addLine, addPolygon, addTextLabel,
} from './mutations';

/**
 * A ready-made floor to start drawing from, for an office that has no floorplan yet.
 *
 * Uploading GeoJSON needs a floorplan to exist somewhere else first. Somebody setting up a
 * small office has nothing to upload, and a blank canvas with no border, no rooms and no desks
 * is a harder place to start than a plan that already shows what each thing looks like and can
 * be reshaped, moved or deleted.
 *
 * Built with the editor's own creation steps, so every feature is exactly what the editor would
 * have drawn — the "[" desk shape under every desk, the same identifiers, the same layers — and
 * it serialises and saves like any other plan.
 *
 * Drawn in metres (one unit is one metre, and the plan says so), so desk sizes, spacing and
 * wall thickness mean what they say from the first edit.
 *
 * The desks are not in a room yet. Linking a desk to a room is what makes it bookable, and the
 * office's rooms are the administrator's to choose: guessing would add seats to real rooms'
 * capacity without anybody deciding it. The editor marks every unassigned desk, so none of them
 * goes unnoticed.
 */

/** One unit of the sample is one metre. */
export const SAMPLE_UNITS_PER_METRE = 1;

/** The words written on the sample floor, in the administrator's language. */
export interface SampleFloorPlanNames {
  readonly openOffice: string;
  readonly meetingRoom: string;
  readonly focusRoom: string;
  readonly kitchen: string;
  readonly entrance: string;
}

type Step = (floorPlan: EditableFloorPlan) => EditableFloorPlan;

/** An interior wall, 15 cm: what a partition is, and thick enough to read at any zoom. */
const WALL_THICKNESS_METRES = 0.15;

const rectangle = (left: number, bottom: number, right: number, top: number): MultiPolygonCoordinates => [[[
  [left, bottom], [right, bottom], [right, top], [left, top], [left, bottom],
]]];

const area = (
  layer: 'border' | 'room' | 'floor',
  coordinates: MultiPolygonCoordinates,
  elementType: StructuralType | null = null,
): Step => (floorPlan) => addPolygon(floorPlan, layer, coordinates, elementType).floorPlan;

const wall = (from: Coordinate, to: Coordinate): Step => (floorPlan) => (
  addLine(floorPlan, from, to, WALL_THICKNESS_METRES, WALL_THICKNESS_METRES)?.floorPlan ?? floorPlan
);

const desks = (centre: Coordinate, kind: DeskGroupKind): Step => (floorPlan) => addDeskGroup(
  floorPlan,
  deskGroupSeats(centre, { ...DEFAULT_GROUP_OPTIONS, kind, footprint: DEFAULT_FOOTPRINT }, SAMPLE_UNITS_PER_METRE),
  null,
).floorPlan;

const text = (at: Coordinate, words: string): Step => (floorPlan) => addTextLabel(floorPlan, at, words).floorPlan;

const icon = (at: Coordinate, iconName: string): Step => (floorPlan) => addIconLabel(floorPlan, at, iconName).floorPlan;

/**
 * The sample floor: a 30 × 20 m building with an open office of eight desks, a meeting room
 * with its table, a focus room of two desks, and a kitchen by the entrance.
 */
export const sampleFloorPlan = (
  officeId: string,
  storey: number,
  names: SampleFloorPlanNames,
): EditableFloorPlan => {
  const empty: EditableFloorPlan = {
    floorPlanId: '',
    officeId,
    storey,
    unitsPerMetre: SAMPLE_UNITS_PER_METRE,
    border: [],
    rooms: [],
    floorBackdrop: [],
    floorLines: [],
    desks: [],
    deskShapes: [],
    labels: [],
    layerEnvelopes: { border: {}, floor: {}, room: {}, workplaces: {}, label: {} },
  };

  const steps: Step[] = [
    // The building, and the three rooms inside it.
    area('border', rectangle(0, 0, 30, 20)),
    area('room', rectangle(1, 1, 18, 11)),
    area('room', rectangle(19, 1, 29, 9)),
    area('room', rectangle(19, 10, 29, 19)),

    // Two benches of four in the open office, and a pair of desks in the focus room.
    desks([5.5, 6], 'bench4'),
    desks([13.5, 6], 'bench4'),
    desks([24, 14.5], 'pair'),

    // The meeting room's table and the kitchen's counter: furniture, not desks.
    area('floor', rectangle(21.5, 3.5, 26.5, 6.5), 'furniture'),
    area('floor', rectangle(1.5, 17.6, 9.5, 18.6), 'furniture'),

    // The kitchen's partition, with a gap on the right to walk through.
    wall([1, 12], [8, 12]),
    wall([10, 12], [10, 19]),

    text([9.5, 10.2], names.openOffice),
    text([24, 8.2], names.meetingRoom),
    text([24, 18.2], names.focusRoom),
    text([5.5, 13], names.kitchen),
    text([14.5, 18.2], names.entrance),

    icon([24, 2], 'projector'),
    icon([3, 15.5], 'coffee'),
    icon([5.5, 15.5], 'silverware-fork-knife'),
    icon([14.5, 19.2], 'door'),
    icon([12.5, 13], 'printer'),
    icon([16.5, 13], 'toilet'),
    icon([0.8, 19.2], 'fire-extinguisher'),
  ];

  return steps.reduce((floorPlan, step) => step(floorPlan), empty);
};

export default sampleFloorPlan;
